import { t } from 'i18next';
import {
  Fragment,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AuraNextMedicalInterviewConfig } from '@breathelife/auranext';
import { ExternalScriptDomElementPosition, useExternalScriptUrl } from '@breathelife/react-hooks';
import {
  Language,
  MedicalInterviewProvider,
  ParticipantMedicalInterviewStatus,
  QuestionnaireVersionFlag,
} from '@breathelife/types';

import { useNavigate } from 'react-router-dom';
import { useFullPageLoaderContext } from '../Components/LoadingView/FullPageLoader';
import { useAAQuestionnaireVersionContext, useCarrierContext, useDispatch } from '../Hooks';
import { notificationSlice } from '../ReduxStore/Notification/NotificationSlice';
import ApiService from '../Services/ApiService';
import { useParticipantsContext } from './ParticipantsContextProvider';

export type AuraNextMedicalInterviewValue = {
  isEnabled: boolean;
  isLoaded: boolean;
  canSubmitInterview: boolean;
  startInterview: (participantId: string) => void;
  saveOrSubmitInterviewToProvider: () => void;
  isSavingInterview: boolean;
  areAllMedicalInterviewCompleted: boolean;
  isStartingInterview: boolean;
  config?: AuraNextMedicalInterviewConfig;
  error: 'authenticationError' | 'unknownError' | null;
};

export const AuraNextMedicalInterviewContext = createContext<AuraNextMedicalInterviewValue | undefined>(undefined);

type AuraNextMedicalInterviewContextProviderProps = {
  applicationId: string;
  children: ReactNode;
};

export function AuraNextMedicalInterviewContextProvider(
  props: PropsWithChildren<AuraNextMedicalInterviewContextProviderProps>,
): ReactElement | null {
  const { applicationId } = props;
  const { flags } = useAAQuestionnaireVersionContext();
  const { features } = useCarrierContext();
  const dispatch = useDispatch();
  const { participants } = useParticipantsContext();
  const { openFullPageLoader, closeFullPageLoader } = useFullPageLoaderContext();
  const navigate = useNavigate();

  const [savedToken, setSavedToken] = useState<string | null>(null);
  const [savedTokenFetchTime, setSavedTokenFetchTime] = useState<number>(0);
  const [isDeuiReady, setIsDeuiReady] = useState(false);
  const [isSavingInterview, setIsSavingInterview] = useState(false);
  const [canSubmitInterview, setCanSubmitInterview] = useState(false);
  const [isStartingInterview, setIsStartingInterview] = useState(false);
  const [error, setError] = useState<'authenticationError' | 'unknownError' | null>(null);
  const isSubmittingRef = useRef(false);

  const isAuraNextEnabledInQuestionnaire = useMemo(() => {
    return flags.includes(QuestionnaireVersionFlag.AuraNextMedicalInterview);
  }, [flags]);

  const auraNextMedicalInterviewconfig = useMemo(
    () =>
      features.medicalInterview.enabled &&
      features.medicalInterview.provider === MedicalInterviewProvider.AuraNext &&
      flags.includes(QuestionnaireVersionFlag.AuraNextMedicalInterview)
        ? features.medicalInterview.auraNext
        : undefined,
    [features.medicalInterview, flags],
  );

  const { isLibLoaded: isDeuiLibLoaded, lib: deui } = useExternalScriptUrl<any>(
    {
      id: 'aura-next-disclosures-js',
      domElementPosition: ExternalScriptDomElementPosition.head,
      attributes: {
        src: `${auraNextMedicalInterviewconfig?.deuiAppConfigdeUrl}/deui/app.js` || '',
        async: true,
        type: 'text/javascript',
      },
      setLibOnLoad: true,
      onLoadLibName: 'deui',
    },
    !(isAuraNextEnabledInQuestionnaire && auraNextMedicalInterviewconfig),
  );

  const storeInterviewResult = useCallback(
    async (response: Record<string, any>, status: ParticipantMedicalInterviewStatus) => {
      try {
        const participantId = response?.deContext?.sessionId; // This is AuraNext-specific, but allows us not to rely on a state stored participantId
        if (!participantId) {
          throw new Error('Missing participantId in interview result');
        }

        await ApiService.saveMedicalInterviewResponse({
          participantId,
          response,
        });

        await ApiService.updateMedicalInterviewStatus(participantId, status);
      } catch (error) {
        dispatch(
          notificationSlice.actions.setError({
            message: t('notifications.failedToSaveMedicalInterviewResponse'),
            autoHideDuration: 3000,
          }),
        );
      } finally {
        setIsSavingInterview(false);
        navigate(0);
      }
    },
    [dispatch, navigate],
  );

  useEffect(() => {
    if (!isDeuiLibLoaded || !deui || !isAuraNextEnabledInQuestionnaire || !auraNextMedicalInterviewconfig) return;
    deui.App.setUserConfig({
      tenant: auraNextMedicalInterviewconfig.tenant,
    });
    deui.App.setConfig({
      deUrl: auraNextMedicalInterviewconfig.deuiAppConfigdeUrl,
      allowModificationsAfterSubmit: true,
      openToQaReview: false,
      showButtons: true,
      autoScrollToQuestion: true,
      useDateValidation: true,
      displayHelpText: true,
      unexpectedConditionOccured: (error: any) => {
        // Check if it's an authentication-related error
        const isAuthError =
          error?.message?.includes('401') ||
          error?.message?.includes('403') ||
          error?.message?.includes('auth') ||
          error?.message?.includes('expired') ||
          error?.message?.includes('Token has expired') ||
          error?.message?.includes('UNAUTHORIZED');

        // Prioritize authentication errors: once set, keep it even if unknown errors follow
        setError((currentError) => {
          if (currentError === 'authenticationError') {
            return 'authenticationError';
          }
          return isAuthError ? 'authenticationError' : 'unknownError';
        });

        // Return true to indicate we're handling the error (otherwise we get an alert);
        // That's what their SDK docs says.
        return true;
      },
      onSaveComplete: async (result: Record<string, any>) => {
        await storeInterviewResult(result, 'STARTED');
      },
      onInterviewComplete: async (result: Record<string, any>) => {
        isSubmittingRef.current = false;
        await storeInterviewResult(result, 'COMPLETED');
        setCanSubmitInterview(false);
      },
      onInterviewStatusChange: (status: any) => {
        // When submitting and RGA returns follow-up questions,
        // onInterviewComplete won't fire. Detect this case and
        // keep the questionnaire open on the new confirmation section.
        if (isSubmittingRef.current && !status.canBeSubmitted) {
          isSubmittingRef.current = false;
          setIsSavingInterview(false);
          closeFullPageLoader();
        }
        setCanSubmitInterview(status.canBeSubmitted);
        // Interview is ready when status changes
        setIsStartingInterview(false);
      },
    });
    setIsDeuiReady(true);
  }, [
    isDeuiLibLoaded,
    deui,
    auraNextMedicalInterviewconfig,
    isAuraNextEnabledInQuestionnaire,
    storeInterviewResult,
    closeFullPageLoader,
  ]);

  const areAllMedicalInterviewCompleted = useMemo(() => {
    return participants.length === 0
      ? false
      : participants.every(
          (participant) =>
            participant.medicalInterview?.status === 'COMPLETED' || participant.medicalInterview?.status === 'SKIPPED',
        );
  }, [participants]);

  const fetchAuraNextToken = useCallback(async () => {
    try {
      const newToken = await ApiService.fetchMedicalInterviewToken();
      setSavedToken(newToken);
      setSavedTokenFetchTime(Date.now());
      return newToken;
    } catch (error) {
      dispatch(notificationSlice.actions.setError({ message: t('notifications.failedToLoadMedicalInterview') }));
      return null;
    }
  }, [dispatch, setSavedToken, setSavedTokenFetchTime]);

  const startInterview = useCallback(
    async (participantId: string) => {
      if (!auraNextMedicalInterviewconfig) {
        return;
      }

      setIsStartingInterview(true);
      setError(null);

      const shouldRefreshToken = !savedTokenFetchTime || Date.now() - savedTokenFetchTime > 300000;
      const token = shouldRefreshToken ? await fetchAuraNextToken() : savedToken;

      if (!token) {
        setIsStartingInterview(false);
        dispatch(
          notificationSlice.actions.setError({
            message: t('notifications.failedToLoadToken'),
            autoHideDuration: 3000,
          }),
        );
        return;
      }

      try {
        const result = await ApiService.fetchMedicalInterviewInputXml(applicationId, participantId);
        if (!result.inputXml) {
          throw new Error('Missing required interview data');
        }

        const domContainer = 'aura-next-form';
        const correspondenceLanguageToDesiredLocale = {
          [Language.en]: 'en_US',
          [Language.fr]: 'fr_CA',
        };
        const desiredLocale = correspondenceLanguageToDesiredLocale[result.correspondenceLanguage] || 'en_US';

        // Note: startInterview is synchronous, not a promise
        // isStartingInterview will be set to false in onInterviewStatusChange callback
        // The last parameter is the applicantId used in query params - must match the first Party ID in the XML (Party_1)
        deui.App.startInterview(result.inputXml, domContainer, token, desiredLocale, 'Party_1');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to start medical interview:', error);
        setIsStartingInterview(false);
        dispatch(
          notificationSlice.actions.setError({
            message: t('notifications.failedToLoadMedicalInterview'),
            autoHideDuration: 3000,
          }),
        );
      }
    },
    [
      applicationId,
      dispatch,
      savedToken,
      savedTokenFetchTime,
      fetchAuraNextToken,
      auraNextMedicalInterviewconfig,
      deui,
    ],
  );

  const saveOrSubmitInterviewToProvider = useCallback(() => {
    setIsSavingInterview(true);
    openFullPageLoader();

    if (canSubmitInterview) {
      isSubmittingRef.current = true;
      deui.App.submit();
    } else {
      deui.App.save();
    }
  }, [deui, canSubmitInterview, openFullPageLoader]);

  if (!isAuraNextEnabledInQuestionnaire || !auraNextMedicalInterviewconfig) {
    return <Fragment>{props.children}</Fragment>;
  }

  return (
    <Fragment>
      <AuraNextMedicalInterviewContext.Provider
        value={{
          isEnabled: !!(isAuraNextEnabledInQuestionnaire && auraNextMedicalInterviewconfig),
          isLoaded: isDeuiReady,
          canSubmitInterview,
          startInterview,
          saveOrSubmitInterviewToProvider,
          isSavingInterview,
          areAllMedicalInterviewCompleted,
          isStartingInterview,
          error,
          config: auraNextMedicalInterviewconfig,
        }}
      >
        {props.children}
      </AuraNextMedicalInterviewContext.Provider>
    </Fragment>
  );
}

export function useAuraNextMedicalInterviewContext(): AuraNextMedicalInterviewValue {
  const context = useContext(AuraNextMedicalInterviewContext);
  if (typeof context === 'undefined') {
    throw new Error('useAuraNextMedicalInterviewContext must be used within a AuraNextMedicalInterviewContextProvider');
  }
  return context;
}
